import { getEnv, flow, Instance } from "mobx-state-tree";

import { OrganizationRequestInput } from "../../api-models/request/OrganizationRequestInput";
import { UserOrganizationRequestResponse } from "../../api-models/response/user/UserResponse";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { Column, ColumnType } from "../../components/datatable/interfaces";
import { endpoints } from "../api/ApiEndpoints";
import { Option } from "../../components/datatable/interfaces";
import { UserOrganizationRequest } from "../user/my-account/UserOrganizationRequests";
import { OrganizationRequestStatuses, OrganizationRequestTypes } from "../../generated/OrganizationRequests";

export const OrganizationRequestsStorageModel = DataGridStorageModel.named(
  "OrganizationRequestsStorage"
)
  .actions(() => {
    return {
      getOrganizationRequestStatusOptions(): Option[] {
        return Object.entries(OrganizationRequestStatuses)
          .filter((x) => typeof x[1] === "number")
          .map((s) => ({
            label: `organization.request.status.${s[0]}`,
            value: s[1].toString(),
          }));
      },
      getOrganizationRequestTypeOptions(): Option[] {
        return Object.entries(OrganizationRequestTypes)
          .filter((x) => typeof x[1] === "number")
          .map((s) => ({
            label: `organization.request.type.${s[0]}`,
            value: s[1].toString(),
          }));
      },
    };
  })
  .views((self) => ({
    get tableColumns(): Column[] {
      return [
        {
          key: "organizationName",
          name: "organization.DisplayOrganizationName",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
          flex: 2,
        },
        {
          key: "status",
          name: "organization.DisplayOrganizationRequestStatus",
          type: ColumnType.SingleSelect,
          sortable: true,
          filterable: true,
          valueOptions: self.getOrganizationRequestStatusOptions(),
        },
        {
          key: "type",
          name: "organization.DisplayOrganizationRequestType",
          type: ColumnType.SingleSelect,
          sortable: true,
          filterable: true,
          valueOptions: self.getOrganizationRequestTypeOptions(),
        },
        {
          key: "requestDate",
          name: "organization.DisplayOrganizationRequestDate",
          type: ColumnType.DateTime,
          sortable: true,
          filterable: true,
        },
        {
          key: "generalManagerName",
          name: "organization.DisplayGeneralManagerName",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "phone",
          name: "organization.organization-phone",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
      ];
    },
  }))
  .actions((self) => {
    return {
      mapItem(item: UserOrganizationRequestResponse): UserOrganizationRequest {
        return new UserOrganizationRequest(item);
      },
      requestNewOrganization: flow(function* (organizationRequestInput: OrganizationRequestInput) {
        const endpoint = endpoints.get("request_organization");
        return yield getEnv(self).api.post(endpoint, organizationRequestInput);
      }),
      afterCreate() {
        self.defaultSort = { field: "requestDate", sort: "asc" };
        self.gridColumns = self.tableColumns;
      },
    };
  })
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("user_organization_requests");
      endpoint.queryParams = self.getDataQuery();
      return endpoint;
    },
  }));

export type OrganizationRequestsService = Instance<typeof OrganizationRequestsStorageModel>;
